import React, { useMemo } from 'react';
import {
  TableContainer,
  Paper,
  TableHead,
  Checkbox,
  useTheme,
  Table,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
  Input,
  InputAdornment
} from '@mui/material';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import LibraryAddCheckIcon from '@mui/icons-material/LibraryAddCheck';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import {
  IEcommerceProductHierarchy,
  IEcommerceProductHierarchyItem,
  IEcommerceSubBrandHierarchy
} from 'src/models/ecommerce';
import Empty from 'src/components/Empty';
import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Search } from '@mui/icons-material';
interface TableProps {
  data: IEcommerceProductHierarchy[];
  loading: boolean;
  selectedProducts: IEcommerceProductHierarchyItem[];
  onSelectedProductsChange?: (
    products: IEcommerceProductHierarchyItem[]
  ) => void;
  selectedBrand: IEcommerceProductHierarchy[];
  setSelectedBrand: React.Dispatch<
    React.SetStateAction<IEcommerceProductHierarchy[]>
  >;
}

const FilterBrandList: React.FC<TableProps> = ({
  data,
  loading,
  selectedProducts,
  onSelectedProductsChange,
  selectedBrand,
  setSelectedBrand
}) => {
  const theme = useTheme();

  // Helper function to find product by id and originalId
  const isProductSelected = (product: IEcommerceProductHierarchyItem) => {
    return selectedProducts.some(
      (p) => p.id === product.id && p.originalId === product.originalId
    );
  };

  // Helper function to get all products from a subbrand
  const getSubbrandProducts = (subbrand: IEcommerceSubBrandHierarchy) => {
    return subbrand.products || [];
  };

  // Helper function to get all products from a brand
  const getBrandProducts = (brand: IEcommerceProductHierarchy) => {
    const products: IEcommerceProductHierarchyItem[] = [];
    brand.subBrands?.forEach((subbrand) => {
      products.push(...getSubbrandProducts(subbrand));
    });
    return products;
  };

  // Check if all products in a brand are selected
  const isBrandFullySelected = (brand: IEcommerceProductHierarchy) => {
    const products = getBrandProducts(brand);
    if (products.length === 0) return false;
    return (
      products.every((p) => isProductSelected(p)) ||
      selectedBrand.some((item) => item.id === brand.id)
    );
  };

  // Check if some (but not all) products in a brand are selected
  const isBrandIndeterminate = (brand: IEcommerceProductHierarchy) => {
    const products = getBrandProducts(brand);
    if (products.length === 0) return false;
    const selectedCount = products.filter((p) => isProductSelected(p)).length;
    return selectedCount > 0 && selectedCount < products.length;
  };

  // Handle brand checkbox change
  const handleBrandCheckChange = (
    brand: IEcommerceProductHierarchy,
    checked: boolean
  ) => {
    const brandProducts = getBrandProducts(brand);
    let updatedProducts = [...selectedProducts];

    if (checked) {
      // Add all products from brand
      brandProducts.forEach((product) => {
        if (!isProductSelected(product)) {
          updatedProducts.push(product);
        }
      });
      setSelectedBrand([...selectedBrand, brand]);
    } else {
      // Remove all products from brand
      updatedProducts = updatedProducts.filter(
        (selected) =>
          !brandProducts.some(
            (p) => p.id === selected.id && p.originalId === selected.originalId
          )
      );
      setSelectedBrand(selectedBrand.filter((item) => item.id !== brand.id));
    }

    onSelectedProductsChange?.(updatedProducts);
  };

  // Get all products from all brands
  const getAllProducts = () => {
    const allProducts: IEcommerceProductHierarchyItem[] = [];
    data.forEach((brand) => {
      allProducts.push(...getBrandProducts(brand));
    });
    return allProducts;
  };

  // Check if all products are selected
  const isAllProductsSelected = () => {
    const allProducts = getAllProducts();
    if (allProducts.length === 0) return false;
    return allProducts.every((p) => isProductSelected(p));
  };

  // Check if some (but not all) products are selected
  const isSelectAllIndeterminate = () => {
    const allProducts = getAllProducts();
    if (allProducts.length === 0) return false;
    const selectedCount = allProducts.filter((p) =>
      isProductSelected(p)
    ).length;
    return selectedCount > 0 && selectedCount < allProducts.length;
  };

  // Handle select all checkbox change
  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      // Select all products
      const allProducts = getAllProducts();
      onSelectedProductsChange?.(allProducts);
      setSelectedBrand(data);
    } else {
      // Clear all selections
      onSelectedProductsChange?.([]);
      setSelectedBrand([]);
    }
  };

  const addName = useMemo((): Array<IEcommerceProductHierarchy> => {
    return data.map((item) => ({
      ...item,
      name: item.name
    }));
  }, [data]);

  const { filteredList: unsortedFilteredList, handleFilter } =
    useFiltersByName<IEcommerceProductHierarchy>(addName);

  const filteredList = useMemo(() => {
    if (!selectedProducts.length) return unsortedFilteredList;
    if (!unsortedFilteredList.length) return [];

    const activeIdsSet = new Set(selectedProducts.map((item) => item.id));
    return unsortedFilteredList.sort((a, b) => {
      const aIsActive = activeIdsSet.has(a.id);
      const bIsActive = activeIdsSet.has(b.id);
      if (aIsActive && !bIsActive) return -1;
      if (!aIsActive && bIsActive) return 1;
      return 0;
    });
  }, [unsortedFilteredList, selectedProducts]);

  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<IEcommerceProductHierarchy>(filteredList);

  return (
    <>
      <Input
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        fullWidth
        placeholder="Search Brand"
        onChange={(event) => handleFilter(event.target.value)}
      />
      <InfiniteScroll
        dataLength={infiniteScroll.list.length}
        next={fetchMoreData}
        hasMore={infiniteScroll.hasMore}
        loader={<div />}
        height={250}
        endMessage={<div />}
      >
        <TableContainer
          component={Paper}
          sx={{
            mt: theme.spacing(2),
            maxHeight: 480
          }}
        >
          <Table stickyHeader>
            <TableHead
              sx={{
                background: theme.palette.primary.main
              }}
            >
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: '3%' }}>
                  <Checkbox
                    checked={isAllProductsSelected()}
                    indeterminate={isSelectAllIndeterminate()}
                    size="small"
                    onChange={handleSelectAllChange}
                    icon={
                      <FilterNoneIcon
                        sx={{
                          color: theme.palette.common.white,
                          width: theme.spacing(2.5),
                          height: theme.spacing(2.5)
                        }}
                      />
                    }
                    checkedIcon={
                      <LibraryAddCheckIcon
                        sx={{
                          color: theme.palette.common.white,
                          width: theme.spacing(2.5),
                          height: theme.spacing(2.5)
                        }}
                      />
                    }
                    indeterminateIcon={
                      <IndeterminateCheckBoxIcon
                        sx={{
                          color: theme.palette.common.white,
                          width: theme.spacing(2.5),
                          height: theme.spacing(2.5)
                        }}
                      />
                    }
                  />
                </TableCell>
                <TableCell
                  size="small"
                  padding="none"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    flex: 1
                  }}
                >
                  Name
                </TableCell>
                <TableCell
                  size="small"
                  // align="center"
                  padding="none"
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    width: '200px'
                  }}
                >
                  Category
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow hover={false}>
                  <TableCell align="center" colSpan={4}>
                    <Box
                      sx={{
                        height: 300,
                        background: theme.palette.grey[200],
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: theme.palette.grey[200],
                        opacity: '0.3'
                      }}
                    >
                      <CircularProgress
                        disableShrink
                        color="primary"
                        size={64}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : data.length < 0 ? (
                <TableRow hover={false}>
                  <TableCell align="center" colSpan={4}>
                    <Box
                      sx={{
                        height: 300,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Empty message="There's no data yet" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                infiniteScroll.list.map((row) => (
                  <SubBrandListRow
                    row={row}
                    key={row.id}
                    isBrandFullySelected={isBrandFullySelected}
                    isBrandIndeterminate={isBrandIndeterminate}
                    onBrandCheckChange={handleBrandCheckChange}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </InfiniteScroll>
    </>
  );
};

export default FilterBrandList;

function SubBrandListRow(props: {
  row: IEcommerceProductHierarchy;
  isBrandFullySelected: (brand: IEcommerceProductHierarchy) => boolean;
  isBrandIndeterminate: (brand: IEcommerceProductHierarchy) => boolean;
  onBrandCheckChange: (
    brand: IEcommerceProductHierarchy,
    checked: boolean
  ) => void;
}) {
  const { row } = props;
  const isBrandSelected = props.isBrandFullySelected(row);
  const isBrandIndeterminate = props.isBrandIndeterminate(row);

  const handleBrandCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    props.onBrandCheckChange(row, event.target.checked);
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          '&:last-child td, &:last-child th': {
            border: 0
          }
        }}
      >
        <TableCell padding="checkbox" sx={{ width: '3%' }}>
          <Checkbox
            color="primary"
            checked={isBrandSelected}
            indeterminate={isBrandIndeterminate}
            onChange={handleBrandCheckboxChange}
          />
        </TableCell>
        <TableCell padding="none" sx={{ flex: 1 }}>
          {row.name}
        </TableCell>
        <TableCell colSpan={2} padding="none" sx={{ width: '200px' }}>
          {row.category}
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}
